import { useState, useCallback } from 'react'

// ── Storage helpers ────────────────────────────────────────────────────────────

const STORAGE_KEY = 'bim_ca_favorites'

/**
 * Reads favorites from localStorage for the given project.
 *
 * @param {string} projectId - Stable project UUID from `window.bimCadaplusData.projectId`.
 * @returns {Set<string>} Set of favorited product UUIDs (empty Set on error or if none saved).
 */
function loadFavorites(projectId) {
  if (!projectId) return new Set()
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return new Set()
    const all = JSON.parse(raw)
    const uuids = all[projectId]
    return Array.isArray(uuids) ? new Set(uuids) : new Set()
  } catch {
    return new Set()
  }
}

/**
 * Persists the favorites Set for the given project to localStorage.
 * Merges with data for other projects to avoid overwriting them.
 *
 * @param {string}      projectId - Stable project UUID.
 * @param {Set<string>} uuidsSet  - Current favorited UUIDs for this project.
 */
function saveFavorites(projectId, uuidsSet) {
  if (!projectId) return
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    const all = raw ? JSON.parse(raw) : {}
    all[projectId] = [...uuidsSet]
    localStorage.setItem(STORAGE_KEY, JSON.stringify(all))
  } catch {
    // localStorage may be blocked (Safari ITP, incognito) — fail silently
  }
}

// ── Hook ───────────────────────────────────────────────────────────────────────

/**
 * Manages the favorites state for one project, backed by localStorage.
 *
 * State is a `Set<string>` of product UUIDs for O(1) lookup. Components call
 * `isFavorite(uuid)` directly — favorites are NOT injected into product objects.
 *
 * Lives in `AppContext` because favorites are cross-domain
 *
 * @param {string|null|undefined} projectId - Stable project UUID.
 * @returns {{
 *   isFavorite:    (uuid: string) => boolean,
 *   toggleFavorite:(uuid: string) => void,
 *   clearFavorites:() => void,
 *   favoritesCount:number,
 *   favoriteUuids: Set<string>,
 * }}
 */
export function useFavorites(projectId) {
  const [favoriteUuids, setFavoriteUuids] = useState(() => loadFavorites(projectId))

  const isFavorite = useCallback(
    uuid => favoriteUuids.has(uuid),
    [favoriteUuids],
  )

  const toggleFavorite = useCallback(
    uuid => {
      setFavoriteUuids(prev => {
        const next = new Set(prev)
        if (next.has(uuid)) {
          next.delete(uuid)
        } else {
          next.add(uuid)
        }
        saveFavorites(projectId, next)
        return next
      })
    },
    [projectId],
  )

  const clearFavorites = useCallback(
    () => {
      const empty = new Set()
      saveFavorites(projectId, empty)
      setFavoriteUuids(empty)
    },
    [projectId],
  )

  return {
    isFavorite,
    toggleFavorite,
    clearFavorites,
    favoritesCount: favoriteUuids.size,
    favoriteUuids,
  }
}
